import axios, { AxiosError, type AxiosRequestConfig } from 'axios';

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';
/** Origin for signed file URLs returned by the API (paths start with /api/v1/files/...). */
export const API_ORIGIN = API_BASE.startsWith('http') ? new URL(API_BASE).origin : '';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
  }

  /** Field-level validation messages keyed by path. */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries(
      (this.details as { path?: string; message?: string }[]).filter((d) => d.path).map((d) => [d.path!, d.message ?? 'Invalid']),
    );
  }

  get reason(): string | undefined {
    return (this.details as { reason?: string } | undefined)?.reason;
  }
}

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(t: string | null) {
  accessToken = t;
}
export function getAccessToken() {
  return accessToken;
}
export function setSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

export const http = axios.create({ baseURL: API_BASE, withCredentials: true, timeout: 30_000 });

http.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  return config;
});

let refreshing: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  refreshing ??= axios
    .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
    .then((r) => {
      const token = r.data?.data?.accessToken as string | undefined;
      setAccessToken(token ?? null);
      return token ?? null;
    })
    .catch(() => {
      setAccessToken(null);
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<{ error?: ApiErrorBody }>) => {
    const original = error.config as AxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;
    const code = error.response?.data?.error?.code;
    const isAuthCall = original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');
    if (status === 401 && !original?._retried && !isAuthCall && (code === 'TOKEN_EXPIRED' || code === 'UNAUTHENTICATED')) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) return http(original);
      onSessionExpired?.();
    } else if (status === 401 && code === 'ACCOUNT_INACTIVE') {
      onSessionExpired?.();
    }
    throw toApiError(error);
  },
);

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  const e = error as AxiosError<{ error?: ApiErrorBody }>;
  if (e.response) {
    const body = e.response.data?.error;
    return new ApiError(e.response.status, body?.code ?? 'HTTP_ERROR', body?.message ?? e.message, body?.details, body?.requestId);
  }
  if (e.code === 'ECONNABORTED') return new ApiError(0, 'TIMEOUT', 'The request timed out. Please try again.');
  return new ApiError(0, 'NETWORK_ERROR', 'Unable to reach the server. Check your connection.');
}

type Params = Record<string, unknown>;

function clean(params?: Params) {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v]),
  );
}

export const api = {
  get: async <T>(url: string, params?: Params) => (await http.get(url, { params: clean(params) })).data.data as T,
  post: async <T>(url: string, body?: unknown) => (await http.post(url, body ?? {})).data.data as T,
  patch: async <T>(url: string, body?: unknown) => (await http.patch(url, body ?? {})).data.data as T,
  put: async <T>(url: string, body?: unknown) => (await http.put(url, body ?? {})).data.data as T,
  delete: async <T>(url: string) => (await http.delete(url)).data.data as T,
  /** Multipart upload; objects/arrays are JSON-encoded, files appended as-is. */
  upload: async <T>(url: string, fields: Record<string, unknown>) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) {
      if (v === undefined || v === null || v === '') continue;
      if (v instanceof Blob) fd.append(k, v, (v as File).name ?? `${k}.jpg`);
      else if (typeof v === 'object') fd.append(k, JSON.stringify(v));
      else fd.append(k, String(v));
    }
    return (await http.post(url, fd)).data.data as T;
  },
};

export function fileUrl(path: string | undefined | null) {
  if (!path) return '';
  return path.startsWith('http') ? path : `${API_ORIGIN}${path}`;
}
